/**
 * Admin Editor Default Configurations
 *
 * Default values for app grid items, phase configurations, and sidebar pages.
 * Used when no user settings exist or when resetting to defaults.
 *
 * Icon names reference lucide-react icons:
 * @see https://lucide.dev/icons/
 */

import type {
  AppGridItem,
  PhaseType,
  PhaseConfig,
  SidebarPage,
  AdminEditorSettings,
  CustomPhaseNames,
} from '../types/adminEditor';

// ============================================================================
// App Grid Items
// ============================================================================

/**
 * Default app grid items for the app launcher.
 * Includes all primary navigation destinations.
 */
export const DEFAULT_APP_GRID_ITEMS: AppGridItem[] = [
  // Primary Navigation Apps
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    route: '/app/dashboard',
    visible: true,
    order: 0,
    isSystem: true,
    description: 'Overview and metrics',
  },
  {
    id: 'epg',
    label: 'Event Planning',
    icon: 'Calendar',
    route: '/app/epg',
    visible: true,
    order: 1,
    isSystem: true,
    description: '12-month event calendar',
  },
  {
    id: 'organizations',
    label: 'Organizations',
    icon: 'Building',
    route: '/app/organizations',
    visible: true,
    order: 2,
    isSystem: true,
    description: 'Manage company records',
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: 'Target',
    route: '/app/campaigns',
    visible: true,
    order: 3,
    isSystem: true,
    description: 'Campaign management',
  },
  {
    id: 'addresses',
    label: 'Addresses',
    icon: 'MapPin',
    route: '/app/addresses',
    visible: true,
    order: 4,
    isSystem: true,
    description: 'Address verification',
  },
  {
    id: 'routes',
    label: 'Routes',
    icon: 'Map',
    route: '/app/routes',
    visible: true,
    order: 5,
    isSystem: true,
    description: 'Route planning',
  },
  {
    id: 'review-routes',
    label: 'Review Routes',
    icon: 'Route',
    route: '/app/review-routes',
    visible: true,
    order: 6,
    isSystem: true,
    description: 'Review planned routes',
  },
  {
    id: 'contacts',
    label: 'Contacts',
    icon: 'Users',
    route: '/app/contacts',
    visible: true,
    order: 7,
    isSystem: true,
    description: 'Contact management',
  },
  {
    id: 'labels',
    label: 'Labels',
    icon: 'Tags',
    route: '/app/labels',
    visible: true,
    order: 8,
    isSystem: true,
    description: 'Label generator',
  },
  {
    id: 'execution',
    label: 'Tracking',
    icon: 'ClipboardCheck',
    route: '/app/execution',
    visible: true,
    order: 9,
    isSystem: true,
    description: 'Execution tracking',
  },
  {
    id: 'handwrytten',
    label: 'Handwrytten',
    icon: 'PenTool',
    route: '/app/handwrytten',
    visible: true,
    order: 10,
    isSystem: true,
    description: 'Handwritten card export',
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: 'StickyNote',
    route: '/app/notes',
    visible: true,
    order: 11,
    isSystem: true,
    description: 'Notes and documentation',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'BarChart3',
    route: '/app/reports/drop-report',
    visible: true,
    order: 12,
    isSystem: true,
    description: 'Weekly reports',
  },

  // Standalone Pages (outside main app shell)
  {
    id: 'vibe-remote',
    label: 'Vibe Remote',
    icon: 'Terminal',
    route: '/vibe-remote',
    visible: true,
    order: 13,
    isSystem: true,
    description: 'Remote terminal session monitor',
  },
  {
    id: 'cli-dashboard',
    label: 'CLI Dashboard',
    icon: 'Monitor',
    route: '/cli-dashboard',
    visible: true,
    order: 14,
    isSystem: true,
    description: 'Remote CLI management',
  },
  {
    id: 'field-management',
    label: 'Field Management',
    icon: 'Settings',
    route: '/app/admin/field-management',
    visible: true,
    order: 15,
    isSystem: true,
    description: 'Pipedrive field configuration',
  },
  {
    id: 'daily-routine',
    label: 'Daily Routine',
    icon: 'Sun',
    route: '/app/daily-routine',
    visible: true,
    order: 16,
    isSystem: true,
    description: 'Daily task launcher',
  },
  {
    id: 'follow-ups',
    label: 'Follow-Ups',
    icon: 'Bell',
    route: '/app/follow-ups',
    visible: true,
    order: 17,
    isSystem: true,
    description: 'Drop follow-up tracking',
  },
  {
    id: 'weekly-reports',
    label: 'Weekly Reports',
    icon: 'FileText',
    route: '/app/reports/call-status',
    visible: true,
    order: 18,
    isSystem: true,
    description: 'Weekly call status reports',
  },
];

// ============================================================================
// Phase Names
// ============================================================================

/**
 * Default display names for each phase type.
 */
export const DEFAULT_PHASE_NAMES: Record<PhaseType, string> = {
  planning: 'Planning',
  execution: 'Execution',
  'post-campaign': 'Post-Campaign',
  admin: 'Admin',
};

// ============================================================================
// Sidebar Pages by Phase
// ============================================================================

/**
 * Default sidebar pages for the Planning phase.
 */
const PLANNING_PAGES: SidebarPage[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    route: '/app/dashboard',
    icon: 'LayoutDashboard',
    visible: true,
    order: 0,
    isSystem: true,
  },
  {
    id: 'epg',
    label: 'Event Planning Grid',
    route: '/app/epg',
    icon: 'Calendar',
    visible: true,
    order: 1,
    isSystem: true,
  },
  {
    id: 'organizations',
    label: 'Organizations',
    route: '/app/organizations',
    icon: 'Building',
    visible: true,
    order: 2,
    isSystem: true,
  },
  {
    id: 'campaigns',
    label: 'Campaigns',
    route: '/app/campaigns',
    icon: 'Target',
    visible: true,
    order: 3,
    isSystem: true,
  },
  {
    id: 'addresses',
    label: 'Addresses',
    route: '/app/addresses',
    icon: 'MapPin',
    visible: true,
    order: 4,
    isSystem: true,
  },
  {
    id: 'routes',
    label: 'Routes',
    route: '/app/routes',
    icon: 'Map',
    visible: true,
    order: 5,
    isSystem: true,
  },
  {
    id: 'review-routes',
    label: 'Review Routes',
    route: '/app/review-routes',
    icon: 'Route',
    visible: true,
    order: 6,
    isSystem: true,
  },
];

/**
 * Default sidebar pages for the Execution phase.
 */
const EXECUTION_PAGES: SidebarPage[] = [
  {
    id: 'contacts',
    label: 'Contacts',
    route: '/app/contacts',
    icon: 'Users',
    visible: true,
    order: 0,
    isSystem: true,
  },
  {
    id: 'labels',
    label: 'Labels',
    route: '/app/labels',
    icon: 'Tags',
    visible: true,
    order: 1,
    isSystem: true,
  },
  {
    id: 'execution',
    label: 'Execution Tracking',
    route: '/app/execution',
    icon: 'ClipboardCheck',
    visible: true,
    order: 2,
    isSystem: true,
  },
  {
    id: 'handwrytten',
    label: 'Handwrytten Export',
    route: '/app/handwrytten',
    icon: 'PenTool',
    visible: true,
    order: 3,
    isSystem: true,
  },
  {
    id: 'daily-routine',
    label: 'Daily Routine',
    route: '/app/daily-routine',
    icon: 'Sun',
    visible: true,
    order: 4,
    isSystem: true,
  },
];

/**
 * Default sidebar pages for the Post-Campaign phase.
 */
const POST_CAMPAIGN_PAGES: SidebarPage[] = [
  {
    id: 'notes',
    label: 'Notes',
    route: '/app/notes',
    icon: 'StickyNote',
    visible: true,
    order: 0,
    isSystem: true,
  },
  {
    id: 'reports',
    label: 'Reports',
    route: '/app/reports/drop-report',
    icon: 'BarChart3',
    visible: true,
    order: 1,
    isSystem: true,
  },
  {
    id: 'follow-ups',
    label: 'Follow-Ups',
    route: '/app/follow-ups',
    icon: 'Bell',
    visible: true,
    order: 2,
    isSystem: true,
  },
  {
    id: 'weekly-reports',
    label: 'Weekly Call Status',
    route: '/app/reports/call-status',
    icon: 'FileText',
    visible: true,
    order: 3,
    isSystem: true,
  },
];

/**
 * Default sidebar pages for the Admin phase.
 */
const ADMIN_PAGES: SidebarPage[] = [
  {
    id: 'field-management',
    label: 'Field Management',
    route: '/app/admin/field-management',
    icon: 'Settings',
    visible: true,
    order: 0,
    isSystem: true,
  },
  {
    id: 'vibe-remote',
    label: 'Vibe Remote',
    route: '/vibe-remote',
    icon: 'Terminal',
    visible: true,
    order: 1,
    isSystem: true,
  },
  {
    id: 'cli-dashboard',
    label: 'CLI Dashboard',
    route: '/cli-dashboard',
    icon: 'Monitor',
    visible: true,
    order: 2,
    isSystem: true,
  },
];

/**
 * Default phase configurations with all sidebar pages organized by workflow stage.
 */
export const DEFAULT_SIDEBAR_PHASES: PhaseConfig[] = [
  {
    type: 'planning',
    label: DEFAULT_PHASE_NAMES.planning,
    pages: PLANNING_PAGES,
    collapsed: false,
  },
  {
    type: 'execution',
    label: DEFAULT_PHASE_NAMES.execution,
    pages: EXECUTION_PAGES,
    collapsed: false,
  },
  {
    type: 'post-campaign',
    label: DEFAULT_PHASE_NAMES['post-campaign'],
    pages: POST_CAMPAIGN_PAGES,
    collapsed: false,
  },
  {
    type: 'admin',
    label: DEFAULT_PHASE_NAMES.admin,
    pages: ADMIN_PAGES,
    collapsed: true,
  },
];

// ============================================================================
// Settings Factory
// ============================================================================

/**
 * Settings schema version.
 * Increment when making breaking changes to the settings structure.
 */
export const SETTINGS_VERSION = 1;

/**
 * Create default AdminEditorSettings object.
 * Use this function to get a fresh copy of default settings.
 *
 * @returns Complete AdminEditorSettings with defaults
 */
export function getDefaultSettings(): AdminEditorSettings {
  return {
    appGridItems: [...DEFAULT_APP_GRID_ITEMS],
    phases: DEFAULT_SIDEBAR_PHASES.map((phase) => ({
      ...phase,
      pages: [...phase.pages],
    })),
    customPhaseNames: {},
    lastModified: new Date().toISOString(),
    version: SETTINGS_VERSION,
  };
}

/**
 * Get default custom phase names (empty object - use DEFAULT_PHASE_NAMES).
 *
 * @returns Empty CustomPhaseNames object
 */
export function getDefaultPhaseNames(): CustomPhaseNames {
  return {};
}

/**
 * Get the display name for a phase, considering custom names.
 *
 * @param type - Phase type
 * @param customNames - Custom phase names override
 * @returns Display name for the phase
 */
export function getPhaseDisplayName(
  type: PhaseType,
  customNames: CustomPhaseNames = {}
): string {
  return customNames[type] || DEFAULT_PHASE_NAMES[type];
}

/**
 * All available page IDs that can be distributed across phases.
 * Used for validation and completeness checks.
 */
export const ALL_PAGE_IDS: string[] = [
  'dashboard',
  'epg',
  'organizations',
  'campaigns',
  'addresses',
  'routes',
  'review-routes',
  'contacts',
  'labels',
  'execution',
  'handwrytten',
  'notes',
  'reports',
  'vibe-remote',
  'cli-dashboard',
  'field-management',
  'daily-routine',
  'follow-ups',
  'weekly-reports',
];

/**
 * All phase types in display order.
 */
export const PHASE_ORDER: PhaseType[] = [
  'planning',
  'execution',
  'post-campaign',
  'admin',
];
